<script setup lang="ts">
import { computed, useAttrs, useId } from 'vue'
import type { InputProps } from '../../types/ui'

interface Props extends InputProps {}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  size: 'md',
  disabled: false,
  readonly: false,
  required: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
  input: [event: Event]
}>()

const attrs = useAttrs()

/*
  useId gives a stable id across renders — the old Math.random() version
  produced a new id on every re-render, which broke label/input association
  as soon as the component updated.
*/
const generated = useId()
const inputId = computed(() => (attrs.id as string) || `input-${generated}`)
const describedBy = computed(() => {
  if (props.error) return `${inputId.value}-error`
  if (props.hint) return `${inputId.value}-hint`
  return undefined
})

const handleInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
  emit('input', event)
}
</script>

<template>
  <div class="field">
    <label v-if="label" :for="inputId" class="eyebrow label">
      {{ label }}
      <span v-if="required" aria-hidden="true">*</span>
    </label>

    <input
      :id="inputId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
      :class="['control', `control-${size}`, { 'has-error': error }]"
      v-bind="$attrs"
      @input="handleInput"
      @blur="emit('blur', $event)"
      @focus="emit('focus', $event)"
    />

    <p v-if="hint && !error" :id="`${inputId}-hint`" class="hint meta">{{ hint }}</p>
    <p v-if="error" :id="`${inputId}-error`" class="error meta" role="alert">
      {{ error }}
    </p>
  </div>
</template>

<style scoped>
.field {
  display: grid;
  gap: 0.45rem;
}
.label {
  color: var(--c-ink-2);
}

.control {
  display: block;
  width: 100%;
  background: var(--c-surface);
  color: var(--c-ink);
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  font-family: inherit;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}
.control::placeholder {
  color: var(--c-ink-3);
}
.control:focus {
  outline: none;
  border-color: var(--c-accent);
}
.control:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.control.has-error {
  border-color: #b4402a;
}

.control-sm {
  padding: 0.4rem 0.65rem;
  font-size: var(--step--1);
}
.control-md {
  padding: 0.6rem 0.8rem;
  font-size: var(--step-0);
}
.control-lg {
  padding: 0.8rem 1rem;
  font-size: var(--step-1);
}

.hint {
  color: var(--c-ink-3);
}
.error {
  color: #b4402a;
}
</style>
